import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/auth/useAuth';
import { Header } from '../components/Header';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { Navigate, Outlet } from 'react-router-dom';
import { LoadingScreen } from '../components/common/LoadingScreen';

// Pages
import { Home } from '../pages/Home';
import { LoginPage } from '../pages/LoginPage';
import { MultiStepSignupForm } from '../components/auth/MultiStepSignupForm';
import { CommunityFeed } from '../pages/CommunityFeed';
import { DailyRoutinePage } from '../pages/DailyRoutinePage';
import { CalendarPage } from '../pages/CalendarPage';
import { SavedItemsPage } from '../pages/SavedItemsPage';
import { SavedJobsPage } from '../pages/saved/SavedJobsPage';
import { SavedCoursesPage } from '../pages/saved/SavedCoursesPage';
import { BlogPage } from '../pages/BlogPage';
import { BlogPostPage } from '../pages/BlogPostPage';
import { JobBoard } from '../pages/JobBoard';
import { JobDetailsPage } from '../pages/JobDetailsPage';
import { ProfilePage } from '../pages/ProfilePage';
import { SettingsPage } from '../pages/SettingsPage';
import { SubscriptionPage } from '../pages/SubscriptionPage';
import { CourseMarketplace } from '../pages/CourseMarketplace';
import { BrowseCoursesPage } from '../pages/courses/BrowseCoursesPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { ContactPage } from '../pages/ContactPage';
import { BusinessPage } from '../pages/BusinessPage';
import { AuthCallback } from '../components/auth/AuthCallback';
import { CourseManagement } from '../pages/admin/CourseManagement';
import { ResumeReviewManagement } from '../pages/admin/ResumeReviewManagement';
import { AddCoursePage } from '../pages/admin/courses/AddCoursePage';
import { EditCoursePage } from '../pages/admin/courses/EditCoursePage';
import { EditModulesPage } from '../pages/admin/courses/EditModulesPage';
import { AdminDashboard } from '../pages/admin/AdminDashboard'; 
import { JobManagement } from '../pages/admin/JobManagement';
import { AdminSettingsPage } from '../pages/admin/AdminSettingsPage';
import { BlogManagement } from '../pages/admin/BlogManagement';
import { LayoffTrackerPage } from '../pages/admin/LayoffTrackerPage';
import { ResumeReviewPage } from '../pages/ResumeReviewPage';
import { UserManagement } from '../pages/admin/UserManagement';
import { CareerChatPage } from '../pages/CareerChatPage';
import { PromoPage } from '../pages/PromoPage';
import { PrestigeDashboard } from '../pages/PrestigeDashboard';
import { EventsPage } from '../pages/EventsPage';
import { EventRegistrationPage } from '../pages/EventRegistrationPage';

export default function AppRoutes() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuth();

  // Show loading screen while checking connection
  if (authLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {(!isHomePage && (isAuthenticated || location.pathname === '/business')) && <Header />}
      <main className={`flex-1 ${!isHomePage ? 'pt-16' : ''}`}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/create-account" element={<MultiStepSignupForm />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/business" element={<BusinessPage />} />
          
          {/* Prestige Dashboard */}
          <Route path="/prestige-club" element={<ProtectedRoute requiresSubscription><PrestigeDashboard /></ProtectedRoute>} />
          
          {/* Events */}
          <Route path="/events" element={<ProtectedRoute requiresSubscription><EventsPage /></ProtectedRoute>} />
          <Route path="/events/:eventId/register" element={<ProtectedRoute requiresSubscription><EventRegistrationPage /></ProtectedRoute>} />
          
          {/* Community - Premium Access */}
          <Route path="/community" element={<ProtectedRoute requiresSubscription><CommunityFeed /></ProtectedRoute>} />
           
          {/* Blog - Premium Access */}
          <Route path="/blog" element={<ProtectedRoute requiresSubscription><BlogPage /></ProtectedRoute>} />
          <Route path="/blog/:postId" element={<ProtectedRoute requiresSubscription><BlogPostPage /></ProtectedRoute>} />
           
          {/* Jobs */}
          <Route path="/jobs" element={<ProtectedRoute requiresSubscription><JobBoard /></ProtectedRoute>} />
          <Route path="/jobs/:jobId" element={<ProtectedRoute requiresSubscription><JobDetailsPage /></ProtectedRoute>} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
            <Route path="daily-routine" element={<DailyRoutinePage />} />
            <Route path="calendar" element={<CalendarPage />} />
          </Route>

          {/* Saved Items */}
          <Route path="/saved" element={<ProtectedRoute><SavedItemsPage /></ProtectedRoute>} />
          <Route path="/saved/jobs" element={<ProtectedRoute><SavedJobsPage /></ProtectedRoute>} />
          <Route path="/saved/courses" element={<ProtectedRoute><SavedCoursesPage /></ProtectedRoute>} />

          {/* Profile & Settings */}
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
           
          {/* Subscription */}
          <Route path="/subscription" element={<SubscriptionPage />} />
           
          {/* Courses */}
          <Route path="/courses" element={<ProtectedRoute requiresSubscription={false}><Outlet /></ProtectedRoute>}>
            <Route index element={<Navigate to="/courses/browse" replace />} />
            <Route path="browse" element={<BrowseCoursesPage />} />
            <Route path="marketplace" element={<ProtectedRoute requiresSubscription><CourseMarketplace /></ProtectedRoute>} />
          </Route>
           
          {/* Contact Page */}
          <Route path="/contact" element={<ContactPage />} />

          {/* Admin Routes - Only available when connected */}
          <Route path="/admin" element={<ProtectedRoute requiresAdmin><Outlet /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="jobs/manage" element={<JobManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="courses">
              <Route index element={<CourseManagement />} />
              <Route path="new" element={<AddCoursePage />} />
              <Route path=":courseId/edit" element={<EditCoursePage />} />
              <Route path=":courseId/modules" element={<EditModulesPage />} />
            </Route>
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="blog" element={<BlogManagement />} />
            <Route path="layoffs" element={<ProtectedRoute requiresSubscription><LayoffTrackerPage /></ProtectedRoute>} />
            <Route path="resume-review" element={<ResumeReviewManagement />} />
          </Route>

          {/* Resume Review Route */}
          <Route path="/resume-review" element={<ProtectedRoute requiresSubscription><ResumeReviewPage /></ProtectedRoute>} />
           
          {/* Career Chat Route */}
          <Route path="/career-chat" element={<ProtectedRoute requiresSubscription><CareerChatPage /></ProtectedRoute>} />

          {/* Catch-all route for 404s - must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
}